import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Users, Phone, Mail, MessageSquare, ArrowUpRight, Check, X,
  Inbox, UserPlus, Trash2, Loader2, Sparkles,
} from "lucide-react";
import ConvertCaptureModal from "@/components/ConvertCaptureModal";

const STATUSES = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "converted", label: "Converted" },
  { key: "archived", label: "Archived" },
];

const STATUS_COLOR = {
  new: "#10B981",
  contacted: "#0EA5E9",
  converted: "#A855F7",
  archived: "#94A3B8",
};

function sourceColor(s) {
  const m = { qr: "#10B981", share_arc: "#0EA5E9", direct: "#94A3B8", sms: "#F59E0B" };
  return m[(s || "").toLowerCase()] || "#6366F1";
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Captures() {
  const [list, setList] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState({});
  const [convertedToast, setConvertedToast] = useState(null);
  const [converting, setConverting] = useState(null);

  const refresh = useCallback(async () => {
    const [l, s] = await Promise.all([
      api.get(`/captures${filter !== "all" ? `?status=${filter}` : ""}`),
      api.get("/captures/summary"),
    ]);
    setList(l.data);
    setSummary(s.data);
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const setStatus = async (id, status) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.patch(`/captures/${id}/status`, { status });
      refresh();
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const convert = (cap) => setConverting(cap);

  const onConverted = (member) => {
    setConverting(null);
    setConvertedToast(member);
    setTimeout(() => setConvertedToast(null), 5000);
    refresh();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this capture? This cannot be undone.")) return;
    await api.delete(`/captures/${id}`);
    refresh();
  };

  return (
    <div className="p-6 lg:p-10" data-testid="captures-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Buyer Captures</div>
          <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight mt-1">First-dibs list</h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Every name that hit "Get first dibs" on your shared reports. UTM attribution shows you which
            yard sign, DM, or QR scan brought each one in.
          </p>
        </div>
      </div>

      {/* Stat cards */}
      {summary && (
        <div className="grid sm:grid-cols-4 gap-4 mb-6">
          <Stat icon={Inbox} label="Total" value={summary.total} data-testid="cap-stat-total" />
          <Stat icon={Sparkles} label="Unread" value={summary.unread} accent="#10B981" data-testid="cap-stat-unread" />
          <Stat icon={UserPlus} label="Converted" value={summary.converted} accent="#A855F7" data-testid="cap-stat-converted" />
          <Stat icon={ArrowUpRight} label="Conv rate" value={`${summary.conversion_rate_pct}%`} data-testid="cap-stat-rate" />
        </div>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="captures-filters">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setFilter(s.key)}
            className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border"
            style={{
              background: filter === s.key ? "var(--ink)" : "transparent",
              color: filter === s.key ? "#fff" : "var(--ink-2)",
              borderColor: filter === s.key ? "var(--ink)" : "var(--line)",
            }}
            data-testid={`captures-filter-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* List */}
      {list === null ? (
        <div className="card text-center py-16 text-[var(--ink-2)]">Loading…</div>
      ) : list.length === 0 ? (
        <div className="card text-center py-16">
          <Users size={32} className="text-[var(--ink-3)] mx-auto mb-3" />
          <h3 className="font-display font-bold text-lg">No captures yet</h3>
          <p className="text-sm text-[var(--ink-2)] mt-2 max-w-md mx-auto">
            Share a Property Analysis Report or a yard-sign QR — interested buyers can drop their number
            on the public report page, and they'll show up here.
          </p>
          <Link to="/app/leads" className="btn-money mt-4 inline-flex" data-testid="cap-empty-go-leads">
            Go to leads <ArrowUpRight size={14} />
          </Link>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="captures-list">
          {list.map((c) => (
            <li
              key={c.id}
              className="card transition hover:shadow-md"
              style={{ background: !c.read ? "rgba(16,185,129,0.04)" : undefined }}
              data-testid={`capture-row-${c.id}`}
            >
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-lg">{c.name}</span>
                    <StatusChip status={c.status} />
                    {!c.read && c.status === "new" && (
                      <span className="chip" style={{ background: "#10B98122", color: "#10B981", borderColor: "#10B98155", fontSize: 10 }}>
                        <span className="chip-dot" style={{ background: "#10B981" }} />
                        unread
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[var(--ink-2)] mt-1.5 flex-wrap">
                    <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:underline" data-testid={`capture-phone-${c.id}`}>
                      <Phone size={13} /> {c.phone}
                    </a>
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:underline">
                        <Mail size={13} /> {c.email}
                      </a>
                    )}
                    <span className="text-[var(--ink-3)]">·</span>
                    <span>{timeAgo(c.created_at)}</span>
                  </div>
                  <div className="text-xs text-[var(--ink-3)] mt-1.5 truncate">
                    Re: <strong className="text-[var(--ink-2)]">{c.source_lead_address || "—"}</strong>
                  </div>
                  {c.message && (
                    <div className="mt-3 rounded-lg p-3 text-sm flex gap-2" style={{ background: "rgba(15,23,42,0.04)" }}>
                      <MessageSquare size={14} className="text-[var(--ink-3)] mt-0.5 shrink-0" />
                      <span>{c.message}</span>
                    </div>
                  )}
                </div>

                {/* Source pill */}
                <div className="text-right shrink-0">
                  <div className="label-eyebrow">Source</div>
                  <span
                    className="chip mt-1"
                    style={{
                      background: sourceColor(c.utm_source) + "22",
                      color: sourceColor(c.utm_source),
                      borderColor: sourceColor(c.utm_source) + "55",
                    }}
                  >
                    <span className="chip-dot" style={{ background: sourceColor(c.utm_source) }} />
                    {c.utm_source || "direct"}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap mt-4 pt-4 border-t border-[var(--line)]">
                {c.status === "new" && (
                  <button type="button" className="btn-outline" onClick={() => setStatus(c.id, "contacted")} disabled={busy[c.id]} data-testid={`capture-action-contacted-${c.id}`}>
                    <Check size={13} /> Mark contacted
                  </button>
                )}
                {c.status !== "converted" && (
                  <button type="button" className="btn-money" onClick={() => convert(c)} disabled={busy[c.id]} data-testid={`capture-action-convert-${c.id}`}>
                    <UserPlus size={13} />
                    Add to Cash Buyers
                  </button>
                )}
                {c.status !== "archived" && (
                  <button type="button" className="btn-outline" onClick={() => setStatus(c.id, "archived")} disabled={busy[c.id]} data-testid={`capture-action-archive-${c.id}`}>
                    <X size={13} /> Archive
                  </button>
                )}
                <button type="button" className="btn-outline text-[var(--warn)] ml-auto" onClick={() => remove(c.id)} disabled={busy[c.id]} data-testid={`capture-action-delete-${c.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {converting && (
        <ConvertCaptureModal
          capture={converting}
          onClose={() => setConverting(null)}
          onConverted={onConverted}
        />
      )}


      {convertedToast && (
        <div
          className="fixed bottom-6 right-6 card border-2 max-w-sm shadow-xl"
          style={{ borderColor: "#A855F7", background: "#fff" }}
          data-testid="capture-converted-toast"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#A855F722", color: "#A855F7" }}>
              <UserPlus size={18} />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold">{convertedToast.name} added to Cash Buyers</div>
              <p className="text-xs text-[var(--ink-2)] mt-1">Set their buy box now so the matcher can rank them against new GO deals.</p>
              <Link to="/app/team" className="btn-outline mt-3 inline-flex" data-testid="capture-toast-go-team">
                Open Power Team <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = "#0B132B", ...rest }) {
  return (
    <div className="card" {...rest}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ background: accent }}>
          <Icon size={18} />
        </div>
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="font-display font-extrabold text-2xl tabular-nums mt-0.5">{value}</div>
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const c = STATUS_COLOR[status] || "#94A3B8";
  return (
    <span className="chip" style={{ background: c + "22", color: c, borderColor: c + "55", fontSize: 10 }}>
      <span className="chip-dot" style={{ background: c }} />
      {status}
    </span>
  );
}
